import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuthContext } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function BetaExpired({ betaExpiry }: { betaExpiry: string | null }) {
  const { user } = useAuthContext();
  const [email, setEmail] = useState(user?.email ?? "");
  const [submitted, setSubmitted] = useState(false);

  const registerMutation = trpc.admin.registerInterest.useMutation({
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (e) => toast.error(e.message),
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      window.location.href = "/";
    },
  });

  const expiryFormatted = betaExpiry
    ? new Date(betaExpiry).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-[#1a4731] flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[#1a2e1a]">Bolso</h1>
        </div>

        {/* Message */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <h2 className="text-xl font-bold text-[#1a2e1a]">Período de testes encerrado</h2>

          <p className="text-muted-foreground text-sm leading-relaxed">
            {expiryFormatted
              ? `O período de testes do Bolso encerrou em ${expiryFormatted}.`
              : "O período de testes do Bolso foi encerrado."}{" "}
            Agradecemos sua participação! Em breve o app estará disponível para todos.
          </p>

          {!submitted ? (
            <div className="space-y-3 pt-2">
              <p className="text-sm font-medium text-[#1a2e1a]">
                Quer ser avisado quando o Bolso for lançado?
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={() => registerMutation.mutate({ email })}
                  disabled={!email || registerMutation.isPending}
                  className="bg-[#1a4731] hover:bg-[#1a4731]/90 shrink-0"
                >
                  {registerMutation.isPending ? "..." : "Avisar"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 justify-center p-3 bg-green-50 rounded-lg">
              <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-700 font-medium">
                Ótimo! Você será avisado quando o Bolso for lançado.
              </p>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => logoutMutation.mutate()}
        >
          Sair da conta
        </Button>
      </div>
    </div>
  );
}
